/**
 * Pagina /tesoreria/ingresos-varios — Sprint 17 Fase 2.
 *
 * Sprint 17 S17-Z15 (forzando rebuild Vercel 2026-05-26):
 *   anular ingreso usa <ModalConfirmar /> en lugar de window.prompt().
 *
 * Registro manual de ingresos que no llegaron por extracto bancario
 * automatico: transferencias avisadas por WhatsApp, multas, ventas de
 * accesorios, aportes voluntarios (el barrio opera solo por transferencias,
 * nunca efectivo). NO toca el flujo por_cobrar / cobrado del Sprint 17
 * (esos se crean automaticamente por solicitudes de recursos).
 *
 * Form inline desplegable + tabla historica con boton anular por
 * fila. Arquitectura View + wrapper para preview dev.
 */
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  anularIngresoVario,
  crearIngresoVario,
  getResumenIngresosVarios,
  listIngresosVarios,
  type EstadoIngreso,
  type IngresoVarioRead,
  type IngresoVarioResumen,
  type OrigenPago,
} from '../../lib/api/ingresos-varios';
import { listCategorias } from '../../lib/api/categorias';
import { CategoriaSelector } from '../../components/tesoreria/CategoriaSelector';
import { listarLotes } from '../../lib/api/lotes';
import type { CategoriaRead, LoteResumen } from '../../lib/api/types';
import { Card } from '../../components/ui/Card';
import { InputFecha } from '../../components/ui/InputFecha';
import { InputMontoCLP } from '../../components/ui/InputMontoCLP';
import { ModalConfirmar } from '../../components/ui/ModalConfirmar';
import { formatCLP, formatFecha } from '../../lib/format';
import { Monto } from '../../components/tesoreria/Monto';
import { hoy } from '../../lib/tiempo';

const ORIGEN_LABEL: Record<OrigenPago, string> = {
  whatsapp: 'WhatsApp',
  transferencia_manual: 'Transferencia manual',
  otro: 'Otro',
};

// Badge por estado. `recibido` = aprobado + cobrado (plata que entró);
// `por_cobrar` = facturado sin pago; `pendiente_revisar` = legacy.
const ESTADO_BADGE: Record<EstadoIngreso, { label: string; clase: string }> = {
  aprobado: { label: 'Recibido', clase: 'bg-emerald-500/15 text-emerald-300' },
  cobrado: { label: 'Cobrado', clase: 'bg-emerald-500/15 text-emerald-300' },
  por_cobrar: { label: 'Por cobrar', clase: 'bg-amber-500/15 text-amber-300' },
  pendiente_revisar: {
    label: 'Por revisar',
    clase: 'bg-white/[0.06] text-text-secondary',
  },
  anulado: { label: 'Anulado', clase: 'bg-red-500/15 text-red-300' },
};

export interface IngresosVariosViewProps {
  ingresos: IngresoVarioRead[];
  categorias: CategoriaRead[];
  lotes: LoteResumen[];
  /** KPIs calculados en backend (recibido vs por cobrar, sin terceros). */
  resumen: IngresoVarioResumen | null;
  loading: boolean;
  error: string | null;
  onCrear: (input: {
    fecha: string;
    categoria_id: string;
    monto: number;
    descripcion: string;
    origen_pago: OrigenPago;
    lote_id: string | null;
  }) => Promise<void>;
  onAnular: (id: string, motivo: string) => Promise<void>;
}

export function IngresosVariosView({
  ingresos,
  categorias,
  lotes,
  resumen,
  loading,
  error,
  onCrear,
  onAnular,
}: IngresosVariosViewProps) {
  const [showForm, setShowForm] = useState(false);
  const [fecha, setFecha] = useState(() =>
    hoy(),
  );
  const [categoriaId, setCategoriaId] = useState('');
  // Sprint 17 S17-Z13: state numérico con InputMontoCLP.
  const [monto, setMonto] = useState<number | null>(null);
  const [descripcion, setDescripcion] = useState('');
  const [origen, setOrigen] = useState<OrigenPago>('transferencia_manual');
  const [loteId, setLoteId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [anulandoId, setAnulandoId] = useState<string | null>(null);
  // Sprint 17 S17-Z15: modal de anulación reemplaza window.prompt().
  const [ingresoAAnular, setIngresoAAnular] =
    useState<IngresoVarioRead | null>(null);
  // Sprint 17 Z21-fix: feedback visible si la anulación falla. Antes
  // el try/finally tragaba el error y el modal volvía silencioso.
  const [errorAnular, setErrorAnular] = useState<string | null>(null);

  const categoriasIngreso = useMemo(
    () => categorias.filter((c) => c.tipo === 'ingreso' && c.activa),
    [categorias],
  );

  // KPIs calculados en backend (services/dashboard_tesoreria.
  // calcular_resumen_ingresos_varios): recibido (barrio, sin terceros),
  // terceros y por_cobrar, cuadrando con la recaudación del dashboard. No se
  // recalculan en cliente. El `total` NO es un recálculo de montos crudos:
  // es solo la composición de presentación de dos KPIs ya cuadrados y
  // disjuntos en backend (recibido + terceros).
  const recibido = resumen?.recibido ?? 0;
  const terceros = resumen?.terceros ?? 0;
  const total = recibido + terceros;
  const porCobrar = resumen?.por_cobrar ?? 0;

  function resetForm() {
    setFecha(hoy());
    setCategoriaId('');
    setMonto(null);
    setDescripcion('');
    setOrigen('transferencia_manual');
    setLoteId('');
    setFormError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!categoriaId) return setFormError('Selecciona una categoría');
    if (monto === null || monto <= 0)
      return setFormError('El monto debe ser mayor que 0');
    if (!descripcion.trim()) return setFormError('Descripción requerida');
    setSubmitting(true);
    try {
      await onCrear({
        fecha,
        categoria_id: categoriaId,
        monto: monto,
        descripcion: descripcion.trim(),
        origen_pago: origen,
        lote_id: loteId || null,
      });
      resetForm();
      setShowForm(false);
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : 'Error al registrar',
      );
    } finally {
      setSubmitting(false);
    }
  }

  function handleAnular(ingreso: IngresoVarioRead) {
    setIngresoAAnular(ingreso);
  }

  async function confirmarAnular(motivo: string) {
    if (!ingresoAAnular) return;
    setAnulandoId(ingresoAAnular.id);
    setErrorAnular(null);
    try {
      await onAnular(ingresoAAnular.id, motivo);
      setIngresoAAnular(null);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setErrorAnular(detail);
      } else {
        setErrorAnular('Error de red al anular');
      }
    } finally {
      setAnulandoId(null);
    }
  }

  function categoriaNombre(id: string): string {
    return categorias.find((c) => c.id === id)?.nombre || '—';
  }

  function loteIdentificador(id: string | null): string {
    if (!id) return '—';
    return lotes.find((l) => l.id === id)?.identificador || '—';
  }

  return (
    <div className="px-4 md:px-8 py-6 space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-orange font-medium">
            Tesorería
          </div>
          <h1 className="text-2xl font-medium text-text-primary mt-1">
            Ingresos varios
          </h1>
          <p className="text-sm text-text-secondary mt-1 max-w-2xl">
            Ingresos que no llegaron por extracto bancario automático
            (transferencias avisadas por WhatsApp, multas, ventas de
            accesorios).
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm(!showForm)}
          className="bg-orange text-white px-4 py-2 rounded text-sm font-medium hover:opacity-90"
        >
          {showForm ? 'Cancelar' : '+ Nuevo ingreso'}
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Cards resumen: recibido (barrio) · terceros · total · por cobrar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="p-4">
            <div className="text-xs uppercase tracking-wide text-text-muted">
              Recibido (barrio)
            </div>
            <div className="text-2xl font-medium text-emerald-300 mt-2">
              {loading ? '—' : <Monto value={recibido} />}
            </div>
            <div className="text-xs text-text-secondary mt-1">
              Plata del barrio que entró (aprobados + cobrados). Excluye
              fondos de terceros.
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="text-xs uppercase tracking-wide text-text-muted">
              Terceros
            </div>
            <div className="text-2xl font-medium text-sky-300 mt-2">
              {loading ? '—' : <Monto value={terceros} />}
            </div>
            <div className="text-xs text-text-secondary mt-1">
              Fondos de paso (muni/JJVV). No son ingreso del barrio; se
              transfieren a su destino.
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="text-xs uppercase tracking-wide text-text-muted">
              Total recibido
            </div>
            <div className="text-2xl font-medium text-text-primary mt-2">
              {loading ? '—' : <Monto value={total} />}
            </div>
            <div className="text-xs text-text-secondary mt-1">
              Recibido del barrio + terceros.
            </div>
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="text-xs uppercase tracking-wide text-text-muted">
              Por cobrar
            </div>
            <div className="text-2xl font-medium text-amber-300 mt-2">
              {loading ? '—' : <Monto value={porCobrar} />}
            </div>
            <div className="text-xs text-text-secondary mt-1">
              Facturado pero aún sin pago entrante.
            </div>
          </div>
        </Card>
      </div>

      {/* Form inline */}
      {showForm && (
        <Card>
          <form
            onSubmit={handleSubmit}
            className="p-4 space-y-3"
            aria-label="Registrar nuevo ingreso vario"
          >
            <div className="text-sm font-medium text-text-primary mb-2">
              Registrar nuevo ingreso
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-text-muted mb-1">
                  Fecha
                </label>
                <InputFecha
                  value={fecha}
                  onChange={setFecha}
                  className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
                />
              </div>
              <div>
                <label className="block text-xs text-text-muted mb-1">
                  Origen del pago
                </label>
                <select
                  value={origen}
                  onChange={(e) => setOrigen(e.target.value as OrigenPago)}
                  className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
                >
                  {(Object.keys(ORIGEN_LABEL) as OrigenPago[]).map((o) => (
                    <option key={o} value={o}>
                      {ORIGEN_LABEL[o]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-text-muted mb-1">
                  Categoría
                </label>
                <CategoriaSelector
                  categorias={categoriasIngreso}
                  value={categoriaId}
                  onChange={setCategoriaId}
                  placeholder="Seleccionar…"
                  className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
                />
              </div>
              <div>
                <label className="block text-xs text-text-muted mb-1">
                  Casa (opcional)
                </label>
                <select
                  value={loteId}
                  onChange={(e) => setLoteId(e.target.value)}
                  className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
                >
                  <option value="">— Sin casa asociada —</option>
                  {lotes.map((l) => (
                    <option key={l.id} value={l.id}>
                      Casa {l.identificador}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-text-muted mb-1">
                  Monto
                </label>
                <InputMontoCLP
                  value={monto}
                  onChange={setMonto}
                  placeholder="0"
                  className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-text-muted mb-1">
                  Descripción
                </label>
                <input
                  type="text"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  placeholder="Multa por escándalo, venta accesorio, aporte voluntario…"
                  className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange"
                />
              </div>
            </div>

            {formError && (
              <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2">
                {formError}
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                disabled={submitting}
                className="px-3 py-2 text-sm text-text-secondary border border-white/[0.08] rounded hover:text-text-primary"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-3 py-2 text-sm font-medium bg-orange text-white rounded hover:opacity-90 disabled:opacity-40"
              >
                {submitting ? 'Registrando…' : 'Registrar ingreso'}
              </button>
            </div>
          </form>
        </Card>
      )}

      {/* Tabla historial */}
      <Card>
        <div className="p-4">
          <div className="text-sm font-medium text-text-primary mb-3">
            Historial
          </div>
          {loading && (
            <div className="text-sm text-text-muted">Cargando…</div>
          )}
          {!loading && ingresos.length === 0 && (
            <div className="text-sm text-text-muted">
              Sin ingresos varios registrados. Usa "+ Nuevo ingreso" para
              comenzar.
            </div>
          )}
          {!loading && ingresos.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-text-muted border-b border-white/[0.08]">
                    <th className="text-left font-medium py-2">Fecha</th>
                    <th className="text-left font-medium py-2">Origen</th>
                    <th className="text-left font-medium py-2">Categoría</th>
                    <th className="text-left font-medium py-2">Casa</th>
                    <th className="text-left font-medium py-2">Descripción</th>
                    <th className="text-left font-medium py-2">Estado</th>
                    <th className="text-right font-medium py-2">Monto</th>
                    <th className="text-right font-medium py-2 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {ingresos.map((i) => {
                    const anulado = i.estado === 'anulado';
                    return (
                      <tr
                        key={i.id}
                        className={`border-b border-white/[0.04] ${
                          anulado ? 'opacity-50' : 'hover:bg-white/[0.02]'
                        }`}
                      >
                        <td className="py-2 text-text-secondary">
                          {formatFecha(i.fecha)}
                        </td>
                        <td className="py-2 text-text-secondary">
                          {i.origen_pago
                            ? ORIGEN_LABEL[i.origen_pago]
                            : '—'}
                        </td>
                        <td className="py-2 text-text-primary">
                          {categoriaNombre(i.categoria_id)}
                        </td>
                        <td className="py-2 text-text-secondary">
                          {loteIdentificador(i.lote_id)}
                        </td>
                        <td className="py-2 text-text-secondary truncate max-w-xs">
                          {i.descripcion}
                        </td>
                        <td className="py-2">
                          <span
                            className={`inline-block text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide font-semibold ${ESTADO_BADGE[i.estado].clase}`}
                          >
                            {ESTADO_BADGE[i.estado].label}
                          </span>
                        </td>
                        <td className="py-2 text-right text-text-primary font-medium">
                          <Monto value={i.monto} />
                        </td>
                        <td className="py-2 text-right">
                          {!anulado && (
                            <button
                              type="button"
                              onClick={() => handleAnular(i)}
                              disabled={anulandoId === i.id}
                              className="text-[11px] text-text-secondary hover:text-state-danger disabled:opacity-40"
                            >
                              Anular
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {ingresoAAnular && (
        <ModalConfirmar
          title="Anular ingreso vario"
          mensaje={`Vas a anular el ingreso "${ingresoAAnular.descripcion.slice(0, 80)}" por ${formatCLP(ingresoAAnular.monto)}. Esta acción registra la anulación con tu motivo y no se puede deshacer.`}
          pedirMotivo
          motivoPlaceholder="Explica brevemente por qué se anula este ingreso…"
          motivoRequerido
          textoConfirmar="Sí, anular ingreso"
          variantConfirmar="danger"
          onConfirmar={confirmarAnular}
          onCancelar={() => {
            setIngresoAAnular(null);
            setErrorAnular(null);
          }}
          error={errorAnular}
        />
      )}
    </div>
  );
}

export function IngresosVariosPage() {
  const [ingresos, setIngresos] = useState<IngresoVarioRead[]>([]);
  const [categorias, setCategorias] = useState<CategoriaRead[]>([]);
  const [lotes, setLotes] = useState<LoteResumen[]>([]);
  const [resumen, setResumen] = useState<IngresoVarioResumen | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancel = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [ing, cats, lots, res] = await Promise.all([
          listIngresosVarios({ incluir_anulados: true }),
          listCategorias({ activa: true }),
          listarLotes(true),
          getResumenIngresosVarios(),
        ]);
        if (cancel) return;
        setIngresos(ing);
        setCategorias(cats);
        setLotes(lots);
        setResumen(res);
      } catch (e) {
        if (cancel) return;
        setError(e instanceof Error ? e.message : 'Error al cargar');
      } finally {
        if (!cancel) setLoading(false);
      }
    }
    load();
    return () => {
      cancel = true;
    };
  }, [refreshKey]);

  async function handleCrear(input: {
    fecha: string;
    categoria_id: string;
    monto: number;
    descripcion: string;
    origen_pago: OrigenPago;
    lote_id: string | null;
  }) {
    await crearIngresoVario({
      ...input,
      estado: 'aprobado',
    });
    setRefreshKey((k) => k + 1);
  }

  async function handleAnular(id: string, motivo: string) {
    await anularIngresoVario(id, motivo);
    setRefreshKey((k) => k + 1);
  }

  return (
    <IngresosVariosView
      ingresos={ingresos}
      categorias={categorias}
      lotes={lotes}
      resumen={resumen}
      loading={loading}
      error={error}
      onCrear={handleCrear}
      onAnular={handleAnular}
    />
  );
}
